// Iter356 — Shared "resume booking after auth" mechanism.
//
// Two complementary carriers keep the original booking destination alive
// across the whole Trip → Login → (mandatory Profile update) → Trip chain:
//   A) a validated internal `next` URL threaded through the auth pages, and
//   C) a short-lived sessionStorage intent (survives the profile-gate redirect
//      which cannot carry query params, and multi-step flows in the same tab).
// The intent is NON-authoritative: on return, price/eligibility/capacity are
// always recomputed by the backend and the booking is NEVER auto-submitted.

const INTENT_KEY = 'eosh_booking_intent';
const INTENT_TTL_MS = 60 * 60 * 1000; // 60 minutes

// Accept ONLY same-origin internal paths (defends against open-redirects).
export const safeInternalPath = (p) => {
  if (!p || typeof p !== 'string') return null;
  if (!p.startsWith('/') || p.startsWith('//') || p.startsWith('/\\')) return null;
  try {
    const u = new URL(p, window.location.origin);
    if (u.origin !== window.location.origin) return null;
    return `${u.pathname}${u.search}${u.hash}`;
  } catch {
    return null;
  }
};

export const saveBookingIntent = (intent) => {
  try {
    const path = safeInternalPath(intent?.returnTo);
    if (!path) return;
    sessionStorage.setItem(INTENT_KEY, JSON.stringify({
      returnTo: path,
      trip_id: intent?.trip_id || null,
      action: intent?.action || 'resume_booking',
      ts: Date.now(),
    }));
  } catch { /* ignore */ }
};

export const readBookingIntent = () => {
  try {
    const raw = sessionStorage.getItem(INTENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || (Date.now() - (parsed.ts || 0)) > INTENT_TTL_MS) {
      sessionStorage.removeItem(INTENT_KEY);
      return null;
    }
    const path = safeInternalPath(parsed.returnTo);
    return path ? { ...parsed, returnTo: path } : null;
  } catch {
    return null;
  }
};

export const clearBookingIntent = () => {
  try { sessionStorage.removeItem(INTENT_KEY); } catch { /* ignore */ }
};

// Build the /login target for a "sign in to continue booking" link. PURE
// (no side effects) — call saveBookingIntent() in the click handler. `resume`
// appends the resumeBooking flag so the trip page re-opens the form once.
export const buildBookingLoginPath = ({ resume = true, extraEmail = null } = {}) => {
  const base = window.location.pathname;
  const sep = base.includes('?') ? '&' : '?';
  const returnTo = resume ? `${base}${sep}resumeBooking=1` : base;
  const q = new URLSearchParams({ next: returnTo });
  if (extraEmail) q.set('email', extraEmail);
  return `/login?${q.toString()}`;
};

// The internal path (with resumeBooking flag) to persist as the intent.
export const bookingReturnTo = ({ resume = true } = {}) => {
  const base = window.location.pathname;
  const sep = base.includes('?') ? '&' : '?';
  return resume ? `${base}${sep}resumeBooking=1` : base;
};

// Resolve where to land the user AFTER a successful auth/profile step.
// Priority: explicit ?next= → saved intent → provided fallback (role home).
export const resolvePostAuthDest = (search, fallback) => {
  try {
    const nextParam = safeInternalPath(new URLSearchParams(search || '').get('next'));
    if (nextParam) return nextParam;
  } catch { /* ignore */ }
  const intent = readBookingIntent();
  if (intent?.returnTo) return intent.returnTo;
  return fallback;
};
